import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Eye } from "lucide-react";
import { LIVE } from "@/constants/testIds";
import UserBadge from "@/components/UserBadge";
import useLivePreview from "@/hooks/useLivePreview";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function LiveCard({ stream }) {
  const { ref, src, onError } = useLivePreview(stream);
  return (
    <div
      ref={ref}
      data-testid={LIVE.card(stream.id)}
      className="relative aspect-[4/5] card-surface overflow-hidden hover-lift group"
    >
      {src ? (
        <img
          src={src}
          onError={onError}
          alt={stream.title || "Untitled Live"}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-300"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-rose-900/30 to-black" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

      <div className="absolute top-3 left-3 flex items-center gap-2 px-2 py-1 bg-rose-600 text-white text-[10px] uppercase tracking-widest font-bold">
        <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" /> LIVE
      </div>

      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur text-white text-xs">
        <Eye size={12} />
        {stream.viewer_count.toLocaleString()}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4">
        <UserBadge
          userRef={stream.host?.id || stream.host?.handle}
          href={`/u/${stream.host?.id}`}
          className="flex items-center gap-2 mb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-md"
          data-testid={`live-card-host-${stream.id}`}
        >
          <Avatar className="w-7 h-7 border border-white/20">
            <AvatarImage src={stream.host?.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-white text-[10px]">
              {stream.host?.display_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-xs text-white/80">{stream.host?.display_name}</span>
          <VerifiedBadge show={stream.host?.is_verified} size="sm" />
          {(stream.shared_desires_count || 0) >= 1 && (
            <span
              className="ml-auto inline-flex items-center gap-0.5 rounded-full bg-rose-500 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5"
              title={`${stream.shared_desires_count} shared desires`}
              data-testid={`live-card-shared-${stream.id}`}
            >
              ✨ {stream.shared_desires_count}
            </span>
          )}
        </UserBadge>
        <div className="text-sm font-medium leading-tight line-clamp-2">{stream.title || "Untitled Live"}</div>
      </div>
    </div>
  );
}
